'use client';
import React, { useRef, useState, useEffect } from "react";
import Subheading from "../Subheading/Subheading";
import Benefits from "../Benefits/Benefits";
import { LanguageType } from '@/lib/types';
import { LanguageDirection } from '@/lib/types';
import { useIsLg } from '@/contexts/BreakpointContext';
// import { useDetectWrap } from '@/hooks/useDetectWrap';

interface Benefit {
	title: string
	content: string
}

interface BenefitColumnsProps {
	benefits: Benefit[]
	showSubheading?: boolean
	textColor?: string
	lgGap?: string
	columnsRef?: React.RefObject<HTMLDivElement | null>
	language?: LanguageType
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
};

const BenefitColumns: React.FC<BenefitColumnsProps> = ({
	benefits,
	textColor,
	showSubheading = true,
	lgGap = "lg:gap-[2.5rem]",
	columnsRef,
	language = "english"
}) => {
	const isLg = useIsLg();
	const thirdBenefitRef = useRef<HTMLDivElement>(null);
	const [isWrapped, setIsWrapped] = useState(false);
	// const { isWrapped } = useDetectWrap(aspectRatio);


	useEffect(() => {
		const checkIfWrapped = () => {
			if (thirdBenefitRef.current && isLg) {
				const rect = thirdBenefitRef.current.getBoundingClientRect();
				const parentRect = thirdBenefitRef.current.parentElement?.getBoundingClientRect();
				if (parentRect) {
					setIsWrapped(rect.top > parentRect.top + 30); // Adding small threshold
				}
			}
		};

		checkIfWrapped();

		let lastWidth = window.innerWidth;
		const handleResize = () => {
			// Skip height-only changes (mobile browser chrome appearing/disappearing)
			const currentWidth = window.innerWidth;
			if (currentWidth === lastWidth) return;
			lastWidth = currentWidth;
			checkIfWrapped();
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isLg]);

	return (
		<div className="
			flex flex-col
			gap-[2rem] lg:gap-[3.5rem]
			justify-center items-center
			w-full
			">
			{showSubheading && <Subheading size={isLg ? 24 : 20} subheading="LOREM IPSUM" alignment="center" language={language} />}

			{benefits && benefits.length === 3 &&
				<div
					ref={columnsRef}
					style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
					className={`
						min-w-full w-full
						flex flex-col justify-center
						lg:flex-row lg:flex-wrap lg:flex-grow
						gap-[2rem] ${lgGap} lg:justify-between`}>
					<div className="flex flex-grow items-center justify-center">
						<Benefits heading={benefits[0].title} text={benefits[0].content} textColor={textColor} />
					</div>

					{isLg && <div className="w-[1.5px] bg-stroke-soft-200"></div>}

					<div className="flex flex-grow items-center justify-center">
						<Benefits heading={benefits[1].title} text={benefits[1].content} textColor={textColor} />
					</div>

					{isLg && !isWrapped && <div className="w-[1.5px] bg-stroke-soft-200"></div>}

					<div ref={thirdBenefitRef} className="flex flex-grow items-center justify-center">
						<Benefits heading={benefits[2].title} text={benefits[2].content} textColor={textColor} />
					</div>
				</div>
			}

		</div>
	);
}

export default BenefitColumns;
